import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Snowflake, Check } from 'lucide-react';

// Admin card (System page): threshold for "cold" owner engagement.
export const EngagementSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState('30');
  const [coldCount, setColdCount] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getEngagementSettings();
      setDays(String(res.data.cold_days));
    } catch { toast.error('Failed to load engagement settings'); }
    try {
      const c = await api.getColdEngagement();
      setColdCount((c.data.deals || []).length);
    } catch { /* non-admin — count stays hidden */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = parseInt(days, 10);
    if (isNaN(n) || n < 1 || n > 365) { toast.error('Enter a number of days between 1 and 365'); return; }
    setSaving(true);
    try {
      const res = await api.saveEngagementSettings({ cold_days: n });
      setDays(String(res.data.cold_days));
      toast.success(`Deals now count as cold after ${res.data.cold_days} days without owner contact`);
      try {
        const c = await api.getColdEngagement();
        setColdCount((c.data.deals || []).length);
      } catch { /* ignore */ }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="engagement-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Snowflake className="w-4 h-4 text-cyan-400" />
        <p className="font-heading text-base text-foreground">Owner Engagement — Cold Threshold</p>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Deals in Contacted or later without an owner/shareholder meeting or call for this many days are
        flagged as <span className="text-cyan-400">Cold</span> on the Who's Turn page and in the Monday Update email.
      </p>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Cold after</span>
          <Input type="number" min={1} max={365} value={days} onChange={e => setDays(e.target.value)}
            data-testid="cold-days-input"
            className="bg-secondary border-border text-foreground rounded-sm h-8 text-sm w-20" />
          <span className="text-xs text-muted-foreground">days without owner contact</span>
          <Button onClick={save} disabled={saving} data-testid="cold-days-save-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-3">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
            Save
          </Button>
          {coldCount !== null && (
            <span className="text-[11px] text-muted-foreground ml-1" data-testid="cold-count-hint">
              Currently cold: <span className={coldCount > 0 ? 'text-cyan-400 font-medium' : 'text-emerald-400'}>{coldCount} deal{coldCount !== 1 ? 's' : ''}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
